import { StatesModule } from '@uirouter/angular';
import { Transition } from '@uirouter/core';

import { AccessGuardService } from '../auth/services/access-guard.service';
import { CompanyStateService } from '../auth/services/company-state.service';
import { UserStateService } from '../auth/services/user-state.service';
import { SettingsTabsComponent } from './components/settings-tabs/settings-tabs.component';
import { ViewportComponent } from './viewport/viewport.component';
import { UserAddComponent } from './components/user-add/user-add.component';
import { CompanyAddComponent } from './components/company-add/company-add.component';
import { CompanyDetailsComponent } from './components/company-details/company-details.component';
import { CompanyLicensesComponent } from './components/company-licenses/company-licenses.component';
import { UserListComponent } from './components/user-list/user-list.component';
import { UserDetailsComponent } from './components/user-details/user-details.component';
import { SSOSettingsComponent } from './components/sso-settings/sso-settings.component';
import { CapAlertsPageComponent } from './components/cap-alerts/cap-alerts-page/cap-alerts-page.component';
import { CompanyAccessService } from './services/company-access.service';
import { BrandSettingsService } from './services/brand-settings.service';
import { BrandSettingsComponent } from './components/brand-settings/brand-settings.component';
import { IntegrationsComponent } from './components/integrations/integrations.component';
import {
  CAP_ALERTS_INTEGRATION_ID,
  CAP_ALERTS_PAGE_STATE,
  INTEGRATIONS_LIST_STATE,
  INTEGRATIONS_OPEN_STATE,
  INTEGRATIONS_STATE,
  capAlertsReturnTarget,
  companyIntegrationOpenTarget,
} from './services/integrations/integrations-company/integrations-company.routing';
import {
  CAP_ALERTS_REQUIRE_ROLE,
  isCapAlertsPageEnabled,
} from './services/integrations/models/integrations-company.catalog';

/**
 * Settings children that skip the parent admin gate.
 * Keep this list explicit — new `apps.settings.*` states fail closed by default.
 * Nested integrations children (`list` / `open`) inherit via prefix.
 */
const SETTINGS_ADMIN_BYPASS = [INTEGRATIONS_STATE] as const;

const SETTINGS_BILLING_PREFIX = 'apps.settings.billing';

function isSettingsAdminBypass(transition: Transition): boolean {
  const name = transition.to().name ?? '';
  return SETTINGS_ADMIN_BYPASS.some(
    (allowed) => name === allowed || name.startsWith(`${allowed}.`),
  );
}

function isSettingsBillingRoute(transition: Transition): boolean {
  const name = transition.to().name ?? '';
  return (
    name === SETTINGS_BILLING_PREFIX ||
    name.startsWith(`${SETTINGS_BILLING_PREFIX}.`)
  );
}

const requireAdminAccess = {
  token: 'requireAdminAccess',
  deps: [Transition, AccessGuardService],
  resolveFn: (transition: Transition, accessGuardService: AccessGuardService) =>
    accessGuardService.requireAdminAccess(transition),
};

const requireAdminOrOwnUserAccess = {
  token: 'requireAdminAccess',
  deps: [Transition, AccessGuardService],
  resolveFn: (transition: Transition, accessGuardService: AccessGuardService) =>
    accessGuardService.requireAdminOrOwnUserAccess(transition),
};

const requireSettingsAdminAccess = {
  token: 'requireAdminAccess',
  deps: [Transition, AccessGuardService],
  resolveFn: (
    transition: Transition,
    accessGuardService: AccessGuardService,
  ) => {
    if (isSettingsAdminBypass(transition)) {
      return Promise.resolve();
    }
    if (isSettingsBillingRoute(transition)) {
      return accessGuardService.requireBillingAccess(transition);
    }
    return accessGuardService.requireAdminAccess(transition);
  },
};

/** Sends users without a CAP Alerts role to the integrations list. */
function requireCapAlertsRole(
  transition: Transition,
  userStateService: UserStateService,
) {
  if (userStateService.hasPermission(CAP_ALERTS_REQUIRE_ROLE)) {
    return;
  }
  transition.abort();
  return transition.router.stateService.go(INTEGRATIONS_LIST_STATE);
}

export const routes: StatesModule = {
  states: [
    {
      name: 'apps.settings',
      abstract: true,
      component: SettingsTabsComponent,
      resolve: [requireSettingsAdminAccess],
    },
    {
      name: 'apps.settings.home',
      url: '/settings',
      redirectTo: (transition: Transition) => {
        const userStateService = transition.injector().get(UserStateService);
        if (
          !userStateService.hasPermission('sa ua') &&
          userStateService.canAccessBilling()
        ) {
          return 'apps.settings.billing.home';
        }
        return 'apps.settings.company-details';
      },
    },
    {
      name: 'apps.settings.company-details',
      url: '/company/details',
      component: CompanyDetailsComponent,
      resolve: [
        requireAdminAccess,
        {
          token: 'companyId',
          deps: [CompanyStateService],
          resolveFn: (companyStateService: CompanyStateService) => {
            return companyStateService.getSelectedCompanyId();
          },
        },
      ],
    },
    {
      name: 'apps.settings.brand-settings',
      url: '/company/branding',
      component: BrandSettingsComponent,
      resolve: [
        requireAdminAccess,
        {
          token: 'brandSettingsReleased',
          deps: [Transition, BrandSettingsService],
          resolveFn: (
            transition: Transition,
            brandSettingsService: BrandSettingsService,
          ) => {
            if (!brandSettingsService.isReleased()) {
              transition.abort();
              return transition.router.stateService.go(
                'apps.settings.company-details',
                transition.params(),
              );
            }
          },
        },
      ],
    },
    {
      name: 'apps.settings.sso-settings',
      url: '/sso',
      component: SSOSettingsComponent,
      resolve: [
        requireAdminAccess,
        {
          token: 'ssoSettings',
          deps: [Transition, CompanyStateService],
          resolveFn: (
            transition: Transition,
            companyStateService: CompanyStateService,
          ) => {
            if (companyStateService.isSelectedCompanySsoInherited()) {
              transition.abort();
              return transition.router.stateService.go(
                'apps.settings.company-details',
                transition.params(),
              );
            }
          },
        },
      ],
    },
    {
      name: 'apps.settings.company-licenses',
      url: '/company/licenses',
      component: CompanyLicensesComponent,
      resolve: [
        requireAdminAccess,
        {
          token: 'companyLicenses',
          deps: [Transition, CompanyAccessService],
          resolveFn: (
            transition: Transition,
            companyAccessService: CompanyAccessService,
          ) => {
            if (!companyAccessService.accessCompanyLicenses) {
              transition.abort();
              return transition.router.stateService.go(
                'apps.settings.billing.home',
                transition.params(),
              );
            }
          },
        },
      ],
    },
    {
      // Bypasses parent admin gate via SETTINGS_ADMIN_BYPASS — any authenticated user.
      // Abstract so list / :id keep IntegrationsComponent mounted.
      name: INTEGRATIONS_STATE,
      abstract: true,
      component: IntegrationsComponent,
      redirectTo: INTEGRATIONS_LIST_STATE,
    },
    {
      name: INTEGRATIONS_LIST_STATE,
      url: '/integrations',
    },
    {
      name: INTEGRATIONS_OPEN_STATE,
      url: '/integrations/:integrationId',
      redirectTo: (transition: Transition) => {
        const userStateService = transition.injector().get(UserStateService);
        if (
          transition.params().integrationId === CAP_ALERTS_INTEGRATION_ID &&
          isCapAlertsPageEnabled(userStateService)
        ) {
          return CAP_ALERTS_PAGE_STATE;
        }
      },
      resolve: [
        {
          token: 'requireCapAlertsAccess',
          deps: [Transition, UserStateService],
          resolveFn: (
            transition: Transition,
            userStateService: UserStateService,
          ) => {
            if (
              transition.params().integrationId !== CAP_ALERTS_INTEGRATION_ID
            ) {
              return;
            }
            return requireCapAlertsRole(transition, userStateService);
          },
        },
      ],
    },
    {
      name: 'apps.settings.user-list',
      url: '/user/list',
      params: {
        state: '',
      },
      component: UserListComponent,
      resolve: [requireAdminAccess],
    },

    {
      name: 'apps.company',
      abstract: true,
      component: ViewportComponent,
      resolve: [requireAdminAccess],
    },
    {
      name: 'apps.company.add',
      url: '/company/add',
      component: CompanyAddComponent,
    },

    {
      // Outside apps.settings so the settings tab bar does not render.
      name: 'apps.alerts',
      abstract: true,
      component: ViewportComponent,
    },
    {
      // Gated to DEV and STAGE; elsewhere /alerts opens today's CAP dialog.
      name: CAP_ALERTS_PAGE_STATE,
      url: '/alerts',
      component: CapAlertsPageComponent,
      redirectTo: (transition: Transition) => {
        const userStateService = transition.injector().get(UserStateService);
        if (!isCapAlertsPageEnabled(userStateService)) {
          return companyIntegrationOpenTarget(CAP_ALERTS_INTEGRATION_ID);
        }
      },
      resolve: [
        {
          token: 'requireCapAlertsAccess',
          deps: [Transition, UserStateService],
          resolveFn: requireCapAlertsRole,
        },
        {
          token: 'capAlertsReturnTarget',
          deps: [Transition],
          resolveFn: (transition: Transition) =>
            capAlertsReturnTarget(transition.from(), transition.params('from')),
        },
      ],
    },

    {
      name: 'apps.user',
      abstract: true,
      component: ViewportComponent,
      resolve: [requireAdminOrOwnUserAccess],
    },
    {
      name: 'apps.user.add2',
      url: '/users/add',
      redirectTo: 'apps.user.add',
    },
    {
      name: 'apps.user.add',
      url: '/user/add',
      params: {
        state: '',
      },
      component: UserAddComponent,
      resolve: [
        {
          token: 'companyId',
          deps: [CompanyStateService],
          resolveFn: (companyStateService: CompanyStateService) => {
            return companyStateService.getSelectedCompanyId();
          },
        },
      ],
    },
    {
      name: 'apps.user.details',
      url: '/user/details/:username',
      component: UserDetailsComponent,
      params: {
        username: { squash: true, value: '' },
      },
    },
  ],
};
